import {
  CallDetailsFragment,
  CallTranscriptionFragment,
} from "@/graphql/generated/graphql";
import { normalizeFullName } from "@/helpers/string/string.helpers";
import { Speaker, SpeakerAvatarVariant } from "./speakers.utility.decl";

/**
 * A helper function that checks if a transcription has speakers.
 * Return true if the transcription is not null, has the right type, and has a non-empty list of speakers.
 * @param transcription - transcription entity from backend
 */
export const hasSpeakers = (
  transcription: CallDetailsFragment["transcription"] | null
): transcription is CallTranscriptionFragment =>
  !!transcription &&
  transcription.__typename === "CallTranscription" &&
  !!transcription.content &&
  Array.isArray(transcription.content.speakers) &&
  transcription.content.speakers.length > 0;

const otherColors: SpeakerAvatarVariant[] = ["yellow", "purple", "pink"];

type SpeakerReducer = [Map<number, Speaker>, number];
type BackendSpeaker = NonNullable<
  NonNullable<
    NonNullable<CallTranscriptionFragment["content"]>["speakers"]
  >[number]
>;
type CallParticipant = NonNullable<CallDetailsFragment["from"]>;
type AgentSpeaker = Extract<BackendSpeaker, { __typename?: "AgentSpeaker" }>;
type VirtualAgentSpeaker = Extract<
  BackendSpeaker,
  { __typename?: "VirtualAgentSpeaker" }
>;
type ExternalSpeaker = Extract<
  BackendSpeaker,
  { __typename?: "ExternalSpeaker" }
>;

function phoneContactFields(speaker: ExternalSpeaker) {
  const entity = speaker.external?.entity;
  if (entity?.__typename !== "PhoneContact") {
    return { fullName: undefined, companyName: undefined };
  }

  return {
    fullName: normalizeFullName(entity.fullName),
    companyName: entity.companyName,
  };
}

function mapAgentSpeaker(speaker: AgentSpeaker): Speaker {
  const entity = speaker.teammate?.entity;
  return {
    variant: "green",
    agentID: speaker.agentID.toString(),
    fullName:
      entity?.__typename === "PhoneTeammate"
        ? normalizeFullName(entity.fullName)
        : undefined,
  };
}

function mapVirtualAgentSpeaker(
  speaker: VirtualAgentSpeaker
): Speaker | undefined {
  const entity = speaker.virtualAgent?.entity;
  if (entity?.__typename !== "VirtualAgent") {
    return undefined;
  }

  return {
    variant: "green",
    agentID: entity.ID.toString(),
    fullName: normalizeFullName(entity.personality.name),
    isVirtualAgent: true,
  };
}

function isKnownExternalParticipant(
  speaker: ExternalSpeaker,
  participant: CallParticipant
) {
  return (
    participant.__typename === "ExternalParticipant" &&
    speaker.number === participant.number
  );
}

function mapOtherExternalSpeaker(
  speaker: ExternalSpeaker,
  otherColorIndex: number
): Speaker {
  return {
    ...phoneContactFields(speaker),
    number: speaker.number,
    variant: otherColors[otherColorIndex % otherColors.length],
  };
}

function reduceAgentSpeaker(
  [mapper, otherColorIndex]: SpeakerReducer,
  speaker: AgentSpeaker
): SpeakerReducer {
  mapper.set(speaker.channel, mapAgentSpeaker(speaker));
  return [mapper, otherColorIndex];
}

function reduceVirtualAgentSpeaker(
  [mapper, otherColorIndex]: SpeakerReducer,
  speaker: VirtualAgentSpeaker
): SpeakerReducer {
  const mapped = mapVirtualAgentSpeaker(speaker);
  if (mapped) {
    mapper.set(speaker.channel, mapped);
  }
  return [mapper, otherColorIndex];
}

type SpeakerReduction = {
  speaker: BackendSpeaker | null;
  from: CallParticipant;
  to: CallParticipant;
};

function reduceExternalSpeaker(
  [mapper, otherColorIndex]: SpeakerReducer,
  { speaker, from, to }: SpeakerReduction & { speaker: ExternalSpeaker }
): SpeakerReducer {
  if (
    isKnownExternalParticipant(speaker, from) ||
    isKnownExternalParticipant(speaker, to)
  ) {
    mapper.set(speaker.channel, {
      variant: "blue",
      ...phoneContactFields(speaker),
      number: speaker.number,
    });
    return [mapper, otherColorIndex];
  }

  // ExternalSpeaker is neither "from" nor "to"
  mapper.set(
    speaker.channel,
    mapOtherExternalSpeaker(speaker, otherColorIndex)
  );
  return [mapper, otherColorIndex + 1];
}

function reduceSpeaker(
  state: SpeakerReducer,
  reduction: SpeakerReduction
): SpeakerReducer {
  const { speaker } = reduction;
  if (!speaker) {
    return state;
  }

  if (speaker.__typename === "AgentSpeaker") {
    return reduceAgentSpeaker(state, speaker);
  }

  if (speaker.__typename === "VirtualAgentSpeaker") {
    return reduceVirtualAgentSpeaker(state, speaker);
  }

  if (speaker.__typename === "ExternalSpeaker") {
    return reduceExternalSpeaker(state, { ...reduction, speaker });
  }

  return state;
}

/**
 * This function takes transcription.content?.speakers, from and to
 * and return a map of speakers with their channel, variant, agentID, fullName, companyName, number
 * and the index of the other color to use for the next speaker
 */
export type MapToSpeakersOptions = {
  from: CallDetailsFragment["from"] | null;
  to: CallDetailsFragment["to"] | null;
};

export function mapToSpeakers(
  transcription: CallDetailsFragment["transcription"] | null,
  { from, to }: MapToSpeakersOptions
): Map<number, Speaker> | undefined {
  if (!hasSpeakers(transcription) || !from || !to) {
    return undefined;
  }

  const [map] = (transcription.content?.speakers ?? []).reduce<SpeakerReducer>(
    (state, speaker) => reduceSpeaker(state, { speaker, from, to }),
    [new Map(), 0]
  );

  return map;
}
